<script setup lang="ts">
import { ClaimLineStatus } from '@rialtic/types'
import { useClaimFormContext } from '../composables/fields'
import { useHelpers } from '../composables/helpers'
import { useClaimLab } from '../stores/claimLab'
import type { FhirClaimLine, MappedLines, RawInsights } from '../types'
import ClaimLabNonActionableInsights from './ClaimLabNonActionableInsights.vue'
import StatusBar from './StatusBar.vue'
import StatusInsights from './StatusInsights.vue'

const open = defineModel<boolean>('open')
const activeSequence = defineModel<number>('activeSequence')

const { fhirClaimLines, claimLineSequence } = defineProps<{
  allInsights?: boolean
  fhirClaimLines: MappedLines
  testInProgress?: boolean
  claimLineSequence?: Record<number, number>
}>()

const emit = defineEmits<{
  'select-line': [lineNumber: number]
  'submit-form': []
}>()

const claimLab = useClaimLab()
const { state } = useClaimFormContext()
const userPermissions = inject<ComputedRef<string[]>>('user-permissions')
const { isConsoleUser, getExistingStatus, getTestStatus } =
  useHelpers(userPermissions)

const isFiltered = ref(false)
const visibleClaimLines = computed<Map<number, FhirClaimLine | RawInsights>>(
  () => {
    const newClaimLines: MappedLines = new Map()

    if (isFiltered.value) {
      return claimLab.testResultsBySequence
        ? claimLab.testResultsBySequence
        : new Map<number, FhirClaimLine>(
            [...fhirClaimLines].filter(
              ([_key, value]) => value.raw_insights.length,
            ),
          )
    }

    for (const sequence of claimLab.newClaimLineSequences) {
      if (state[`is_other_claim-${sequence}`] !== true) {
        const resolvedSequence = claimLineSequence?.[sequence]
          ? claimLineSequence[sequence]
          : sequence
        newClaimLines.set(resolvedSequence, {} as FhirClaimLine)
      }
    }

    return new Map([...fhirClaimLines, ...newClaimLines])
  },
)

const claimLinesWithoutInsightsCount = computed(() => {
  const claimLines = claimLab.testResultsBySequence || fhirClaimLines
  return (
    claimLab.totalClaimLines -
    new Map<number, FhirClaimLine | RawInsights>(
      [...claimLines].filter(([_key, value]) =>
        Array.isArray(value) ? value.length : value.raw_insights.length,
      ),
    ).size
  )
})

const onSelectLine = (lineNumber: number) => {
  activeSequence.value = lineNumber
  emit('select-line', lineNumber)
}
</script>

<template>
  <aside
    class="max-w-screen print:w-200 border-default border-l bg-white shadow transition-[width] duration-300 ease-in-out"
    :class="{
      'w-200 print:w-full': open,
      'w-18.5 print:w-full': !open,
    }"
  >
    <UButton
      v-if="!open"
      class="text-secondary flex-col px-3 py-6 text-center font-medium leading-5 print:hidden"
      :ui="{
        label: 'whitespace-pre-wrap',
      }"
      color="secondary"
      icon="i-ph-caret-left-bold"
      label="Show status details"
      size="xs"
      variant="link"
      @click="open = true"
    />
    <div
      class="sm:w-200 print:w-full! grid h-full w-screen grid-rows-[auto_1fr] text-neutral-900 transition-opacity print:h-auto"
      :class="{
        'opacity-0 print:opacity-100': !open,
        'opacity-100': open,
      }"
    >
      <div class="px-8 pb-4 pt-6">
        <div class="flex items-center justify-between">
          <h2 class="text-h2 font-semibold lg:text-lg">Status Details</h2>
          <UButton
            v-if="open"
            class="print:hidden"
            color="neutral"
            label="Minimize"
            size="sm"
            trailing-icon="i-ph-caret-right-bold"
            variant="ghost"
            @click="open = false"
          />
        </div>
        <div class="mt-4">
          <div class="flex items-center justify-between">
            <div class="flex justify-between">
              Claim Lines ({{ fhirClaimLines?.size }})
            </div>
            <div
              v-if="claimLinesWithoutInsightsCount > 0"
              class="flex items-center space-x-2"
            >
              <span class="text-xs">
                Hide {{ claimLinesWithoutInsightsCount }} claim line{{
                  claimLinesWithoutInsightsCount > 1 ? 's' : ''
                }}
                without insights
              </span>
              <USwitch
                v-model="isFiltered"
                aria-label="Hide claim lines without insights"
                size="sm"
              />
            </div>
          </div>
          <div class="max-w-184 mt-2 overflow-x-auto pb-2">
            <div class="flex flex-nowrap gap-2">
              <p class="hidden print:inline-block">Active Claim Line:</p>
              <div
                v-for="([key, _value], index) in visibleClaimLines"
                :key="`${key}-${index}`"
                class="flex-[0_0_auto]"
                :class="{ 'print:hidden': activeSequence !== key }"
              >
                <button
                  class="text-primary-900 flex items-center justify-between rounded-lg border border-neutral-300 px-4 py-3 text-sm hover:bg-neutral-50"
                  :class="{
                    'bg-primary-50 hover:bg-primary-100 border-primary-200':
                      activeSequence === key,
                  }"
                  type="button"
                  @click="onSelectLine(key)"
                >
                  Claim Line {{ key }}
                  <StatusBar
                    v-if="claimLab.testResult"
                    class="ml-2"
                    :dot="true"
                    :status="
                      getTestStatus(
                        fhirClaimLines?.get(key)?.raw_insights ?? [],
                        claimLab.testResultsBySequence?.get(key),
                      )
                    "
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="overflow-y-scroll border-t border-neutral-300 px-8 py-6 print:overflow-y-visible"
      >
        <div v-if="activeSequence" class="flex items-stretch space-x-3 pb-6">
          <div class="flex-1 print:max-w-prose">
            <h3 class="mb-2">Existing Status</h3>
            <StatusBar
              v-if="claimLab.newClaimLineSequences.has(activeSequence)"
              class="mb-2"
              :status="ClaimLineStatus.NEW_LINE"
            />
            <StatusBar
              v-else
              class="mb-2"
              :status="
                getExistingStatus(
                  fhirClaimLines?.get(activeSequence)?.raw_insights ?? [],
                )
              "
            />
            <h3 v-if="isConsoleUser" class="mb-2">
              Existing Insights ({{
                fhirClaimLines?.get(activeSequence)?.raw_insights.length || 0
              }})
            </h3>
            <StatusInsights
              v-if="
                fhirClaimLines?.has(activeSequence) &&
                fhirClaimLines?.get(activeSequence)?.raw_insights.length
              "
              v-bind="{ isConsoleUser }"
              :insights="
                fhirClaimLines?.get(activeSequence)?.raw_insights || []
              "
            />
            <div
              v-else-if="
                !fhirClaimLines?.get(activeSequence)?.raw_insights.length
              "
              class="text-xs"
            >
              There are no existing insights for this claim line.
            </div>
          </div>
          <div class="border-default border-l" />
          <div class="flex-1 print:max-w-prose">
            <h3 class="mb-2">Test Status</h3>
            <StatusBar
              v-if="claimLab.testResult"
              class="mb-2"
              :status="
                getTestStatus(
                  fhirClaimLines?.get(activeSequence)?.raw_insights ?? [],
                  claimLab.testResultsBySequence?.get(activeSequence),
                )
              "
            />
            <div v-else class="space-y-2 text-xs">
              <h4 class="font-medium">Untested</h4>
              <div>Run test to view</div>
              <UButton
                class="print:hidden"
                :loading="testInProgress"
                color="secondary"
                icon="i-ph-test-tube"
                label="Run test"
                size="sm"
                trailing
                type="submit"
                @click="emit('submit-form')"
              />
            </div>
            <h3 v-if="isConsoleUser && claimLab.testResult" class="mb-2">
              Test Insights ({{
                claimLab.testResultsBySequence?.get(activeSequence)?.length ||
                0
              }})
            </h3>
            <StatusInsights
              v-if="
                claimLab.testResult &&
                claimLab.testResultsBySequence?.has(activeSequence) &&
                claimLab.testResultsBySequence.get(activeSequence)!.length
              "
              v-bind="{ isConsoleUser }"
              :insights="
                claimLab.testResultsBySequence.get(activeSequence) || []
              "
            />
            <div
              v-else-if="
                claimLab.testResult &&
                claimLab.testResultsBySequence?.size &&
                !claimLab.testResultsBySequence.get(activeSequence)?.length
              "
              class="text-xs"
            >
              There are no test insights for this claim line.
            </div>
          </div>
        </div>
      </div>
      <ClaimLabNonActionableInsights
        v-if="claimLab.isAllInsightsEnabled && allInsights"
        :active-sequence="activeSequence"
        :is-console-user="isConsoleUser"
      />
    </div>
  </aside>
</template>
